import { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useFileSystem } from "@/contexts/FileSystemContext";
import { useRealm } from "@/contexts/RealmContext";
import { useSyncStatusStore } from "@/stores/syncStatusStore";

export function URLSync() {
  const { realmSlug, documentSlug } = useParams<{
    realmSlug?: string;
    documentSlug?: string;
  }>();
  const navigate = useNavigate();

  const { getRealmBySlug, activeRealm, setActiveRealmId, realms } = useRealm();
  const { getFileBySlug, setActiveFileId, fileTree, fileMap } = useFileSystem();

  const { isNotesSynced, isFileSystemReady } = useSyncStatusStore();
  const canSync = isNotesSynced && isFileSystemReady;

  const isNavigating = useRef(false);

  useEffect(() => {
    if (!canSync) return;

    if (!realmSlug) {
      if (activeRealm) {
        isNavigating.current = true;
        navigate(`/${activeRealm.slug}`, { replace: true });
        setTimeout(() => {
          isNavigating.current = false;
        }, 100);
      }
      return;
    }

    const realm = getRealmBySlug(realmSlug);
    if (!realm) {
      if (realms.length > 0) {
        isNavigating.current = true;
        navigate(`/${realms[0].slug}`, { replace: true });
        setTimeout(() => {
          isNavigating.current = false;
        }, 100);
      }
      return;
    }

    if (realm.id !== activeRealm?.id) {
      setActiveRealmId(realm.id);
      localStorage.setItem("diegesis_active_realm", realm.id);
    }

    if (documentSlug) {
      const file = getFileBySlug(documentSlug);
      if (file) {
        setActiveFileId(file.id);
      } else {
        isNavigating.current = true;
        navigate(`/${realmSlug}`, { replace: true });
        setTimeout(() => {
          isNavigating.current = false;
        }, 100);
      }
    } else {
      const firstFile = fileTree.find((f) => f.type !== "folder");
      if (firstFile) {
        isNavigating.current = true;
        navigate(`/${realmSlug}/${firstFile.slug}`, { replace: true });
        setTimeout(() => {
          isNavigating.current = false;
        }, 100);
      } else {
        setActiveFileId(null);
      }
    }
  }, [
    canSync,
    realmSlug,
    documentSlug,
    getRealmBySlug,
    getFileBySlug,
    setActiveFileId,
    setActiveRealmId,
    realms,
    fileTree,
    navigate,
    activeRealm,
    fileMap,
  ]);

  return null;
}
